import { useShouldRemoveFileUploadPrefix } from '@modules/common/hooks';
import { getIdOrLabelFromCategory } from '@modules/common/utils';
import { useSearchAnalysesWhereClause } from '@modules/feed/hooks/useSearchAnalysesWhereClause';
import { useSearchStore } from '@modules/feed/store/searchStore';
import { getReportDataPoints } from '@modules/feed/utils';
import { useTaxonomyStore } from '@modules/taxonomy/store/taxonomyStore';
import { TimeRange } from 'v2/core/entities';
import useExecuteEQL from 'v2/core/repository/edge-server/eql/useExecuteEQL';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { EQLFunctionName, ExpressionType } from 'v2/core/repository/eql/types';
import { useGetUser } from 'v2/core/services/user';

const MAX_NUMBER_OF_DATAPOINTS = 5;

export function useFetchTrackedKeywordGroupByCategories() {
  const {
    tenant: { timezone, id: tenantID },
  } = useGetUser();
  const taxonomyID = useTaxonomyStore(({ taxonomyID }) => taxonomyID ?? '');
  const duration = useSearchStore(({ query }) => query.duration);
  const whereClause = useSearchAnalysesWhereClause();
  const shouldRemoveFileUploadPrefix = useShouldRemoveFileUploadPrefix();

  const eqlProps = {
    timezone,
    taxonomyID,
    dateRange: duration as TimeRange,
    whereClause,
    expressionType: ExpressionType.Aggregates,
    groupBy: { entityCategory: true },
    functions: [
      { name: EQLFunctionName.ApplyTaxonomy },
      { name: EQLFunctionName.Count },
    ],
  };

  const { data, status, error, refetch } = useExecuteEQL({
    input: { tenantID, eqlProps },
  });

  const searchReportDataPoints = getReportDataPoints({
    data,
    shouldRemoveFileUploadPrefix,
  })
    .slice(0, MAX_NUMBER_OF_DATAPOINTS)
    .map((dataPoint) => ({
      ...dataPoint,
      id: getIdOrLabelFromCategory({
        key: dataPoint.id,
        category: dataPoint.category,
      }),
      label: getIdOrLabelFromCategory({
        key: dataPoint.label,
        category: dataPoint.category,
      }),
    }));

  return {
    data: searchReportDataPoints,
    loading: IsLoading(status) && !data,
    error: error,
    refetch: refetch,
  };
}
